import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../components/ui/select";
import { ArrowLeft, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import PDFFieldEditor from "../components/PDFFieldEditor";

export default function SignatureEditorPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [documents, setDocuments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDocId, setSelectedDocId] = useState(searchParams.get("doc") || "");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [showEditor, setShowEditor] = useState(false);
  const [initialFields, setInitialFields] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [docsRes, templatesRes] = await Promise.all([
        api.get("/documents"),
        api.get("/templates")
      ]);
      setDocuments(docsRes.data);
      setTemplates(templatesRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateChange = (templateId) => {
    setSelectedTemplateId(templateId);
    if (templateId && templateId !== "none") {
      const template = templates.find(t => t.id === templateId);
      setInitialFields(template?.fields || []);
    } else {
      setInitialFields([]);
    }
  };

  const handleStartEditor = () => {
    if (!selectedDocId) {
      toast.error("Veuillez sélectionner un document");
      return;
    }
    setShowEditor(true);
  };

  const handleSaveFields = async (fields) => {
    // Navigate to new signature page with fields
    const fieldsParam = encodeURIComponent(JSON.stringify(fields));
    navigate(`/signatures/new?doc=${selectedDocId}&fields=${fieldsParam}`);
  };

  const selectedDocument = documents.find(d => d.id === selectedDocId);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (showEditor && selectedDocId) {
    return (
      <div className="h-[calc(100vh-200px)]" data-testid="signature-editor-page">
        <PDFFieldEditor
          documentId={selectedDocId}
          documentName={selectedDocument?.name || "Document"}
          initialFields={initialFields}
          onSave={handleSaveFields}
          onCancel={() => setShowEditor(false)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl mx-auto" data-testid="signature-editor-setup">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/signatures")}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Éditeur de champs
          </h1>
          <p className="text-muted-foreground mt-1">
            Positionnez visuellement les champs de signature sur le document
          </p>
        </div>
      </div>

      {/* Setup Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Document Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Document à configurer</label>
            <Select value={selectedDocId} onValueChange={setSelectedDocId}>
              <SelectTrigger data-testid="document-select">
                <SelectValue placeholder="Sélectionnez un document" />
              </SelectTrigger>
              <SelectContent>
                {documents.map((doc) => (
                  <SelectItem key={doc.id} value={doc.id}>
                    {doc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Template Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Template initial (optionnel)</label>
            <Select
              value={selectedTemplateId || "none"}
              onValueChange={handleTemplateChange}
            >
              <SelectTrigger data-testid="template-select">
                <SelectValue placeholder="Partir d'un template..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucun template</SelectItem>
                {templates.map((template) => (
                  <SelectItem key={template.id} value={template.id}>
                    {template.name} ({template.fields?.length || 0} champs)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Vous pourrez modifier les positions dans l'éditeur visuel
            </p>
          </div>

          {/* Preview */}
          {selectedDocument && (
            <div className="p-4 rounded-lg bg-muted">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">{selectedDocument.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {selectedDocument.category} - {(selectedDocument.file_size / 1024).toFixed(1)} Ko
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => navigate("/signatures")}
              className="flex-1"
            >
              Annuler
            </Button>
            <Button
              onClick={handleStartEditor}
              disabled={!selectedDocId}
              className="flex-1"
              data-testid="start-editor-btn"
            >
              Ouvrir l'éditeur visuel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
